import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getRegistrationResponse, submitRegistrationResponse, fmtPublicDateTime,
} from '../api/publicRegistrationApi';
import '../styles/publicForms.css';

/**
 * Landing page for the Keep / Cancel links in "Changes to Registered Event" emails and push
 * notifications. Opening the link never changes anything: the person must press a button
 * (so mail scanners / link previews can't cancel a registration by accident).
 */
export default function RegistrationResponsePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const suggested = (params.get('action') || '').toUpperCase();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [confirmCancel, setConfirmCancel] = useState(suggested === 'CANCEL');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!token) { setState({ loading: false, error: 'This link is not valid.', data: null }); return; }
    let cancelled = false;
    getRegistrationResponse(token)
      .then(data => { if (!cancelled) setState({ loading: false, error: '', data }); })
      .catch(e => { if (!cancelled) setState({ loading: false, error: e.message, data: null }); });
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (action) => {
    setBusy(true); setActionError('');
    try {
      const res = await submitRegistrationResponse(token, action);
      setResult(res);
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const { loading, error, data } = state;
  const primary = data?.org?.primaryColor || '#6B46C1';

  const shell = (title, body) => (
    <div className="pf-page" style={{ '--pf-primary': primary }}>
      <div className="pf-card">
        <div className="pf-head">
          {data?.org?.logoUrl && <img src={data.org.logoUrl} alt="" />}
          <div className="pf-org">{data?.org?.name}</div>
          <h1 className="pf-title">{title}</h1>
        </div>
        <div className="pf-body">{body}</div>
      </div>
    </div>
  );

  if (loading) return shell('Loading…', <div className="pf-center">Please wait…</div>);
  if (error || !data) return shell('Your registration', <div className="pf-alert pf-alert--error">{error || 'This link is not valid.'}</div>);

  const { event } = data;

  if (result) {
    const kept = result.result === 'KEEP';
    return shell(kept ? 'Registration kept' : 'Registration cancelled', (
      <div className="pf-center">
        <div className="pf-big" aria-hidden="true">{kept ? '✅' : '👋'}</div>
        <p>{kept ? `Thank you, ${data.firstName}. We look forward to seeing you at ${event.title}.` : `Your registration for ${event.title} has been cancelled. We hope to see you at a future event.`}</p>
      </div>
    ));
  }

  const changes = data.changes || [];
  const eventBlock = (
    <>
      <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>{event.title}</h2>
      {changes.length > 0 && (
        <>
          <div className="pf-legend">What changed</div>
          <ul className="pf-changes">
            {changes.map(c => (
              <li key={c.field}><strong>{c.label}:</strong> <span className="pf-old">{c.from}</span> → {c.to}</li>
            ))}
          </ul>
        </>
      )}
      <div className="pf-event">
        <strong>When:</strong> {fmtPublicDateTime(event.start)}{event.end ? ` – ${fmtPublicDateTime(event.end)}` : ''}<br />
        {(event.location.name || event.location.address) && (
          <><strong>Where:</strong> {[event.location.name, event.location.address].filter(Boolean).join(', ')}</>
        )}
      </div>
    </>
  );

  if (data.registrationStatus === 'CANCELLED') {
    return shell('Your registration', (<>{eventBlock}<div className="pf-alert pf-alert--info">This registration has been cancelled.</div></>));
  }
  if (!data.canRespond) {
    return shell('Your registration', (<>{eventBlock}<div className="pf-alert pf-alert--info">This registration can no longer be changed online. Please contact the organiser if you need help.</div></>));
  }
  const already = data.response === 'KEEP'
    ? <div className="pf-alert pf-alert--ok">You told us you're keeping this registration. You can still cancel below if your plans change.</div>
    : null;

  return shell(`Hi ${data.firstName}, this event has changed`, (
    <>
      {eventBlock}
      {already}
      {actionError && <div className="pf-alert pf-alert--error" role="alert">{actionError}</div>}
      <p style={{ fontSize: 14 }}>What would you like to do with your registration?</p>

      {!confirmCancel ? (
        <>
          <button className="pf-btn pf-btn--keep" onClick={() => respond('KEEP')} disabled={busy}>
            {busy ? 'Saving…' : 'Keep my registration'}
          </button>
          <button className="pf-btn pf-btn--ghost" onClick={() => setConfirmCancel(true)} disabled={busy}>
            Cancel my registration
          </button>
        </>
      ) : (
        <>
          <div className="pf-alert pf-alert--error">Cancel your registration completely? Your place will be released to someone else.</div>
          <button className="pf-btn pf-btn--ghost" onClick={() => respond('CANCEL')} disabled={busy}>
            {busy ? 'Cancelling…' : 'Yes, cancel my registration'}
          </button>
          <button className="pf-btn pf-btn--keep" onClick={() => setConfirmCancel(false)} disabled={busy}>
            No, go back
          </button>
        </>
      )}
    </>
  ));
}
